<template>
  <div class="dashboard-container">
    <div class="dashboard-header">
      <img src="@/assets/icons/cle.png" alt="clé" class="dashboard-icon" />
      <span class="dashboard-title">Tableau de bord</span>
    </div>
    
    <div v-if="loading" class="dashboard-loading">
      <div class="loading-spinner"></div>
      <p>Chargement du tableau de bord...</p>
    </div>
    
    <div v-else-if="error" class="dashboard-error">
      <div class="error-icon">⚠️</div>
      <p>{{ error }}</p>
      <button @click="loadDashboardData" class="retry-button">Réessayer</button>
    </div>
    
    <div v-else class="dashboard-content">
      <div class="dashboard-row">
        <div class="dashboard-card dashboard-card-large">
          <div class="dashboard-card-title">▪ Contrats Actifs</div>
          <div class="dashboard-card-value">
            <span class="big">{{ dashboardData?.contratsActifs ?? '--' }}</span>/<span class="total">{{ dashboardData?.totalBiens ?? '--' }}</span>
          </div>
          <router-link to="/contrats" class="dashboard-link">Voir tout</router-link>
        </div>
        <div class="dashboard-card">
          <div class="dashboard-card-title">▪ Contrats Actifs</div>
          <div class="dashboard-list">
            <div v-for="contrat in dashboardData?.contrats || []" :key="contrat.nom" class="dashboard-list-row">
              <span>{{ contrat.nom }}</span>
              <span>{{ contrat.echeance }}</span>
              <span class="badge badge-orange">{{ contrat.badge }}</span>
            </div>
          </div>
          <router-link to="/contrats" class="dashboard-link">Voir tout</router-link>
        </div>
      </div>
      <div class="dashboard-row">
        <div class="dashboard-card dashboard-card-full">
          <div class="dashboard-card-title">▪ Prochains Contrats à Échéance</div>
          <div class="dashboard-list">
            <template v-if="dashboardData?.echeances?.length">
              <div v-for="echeance in dashboardData.echeances" :key="echeance.nom" class="dashboard-list-row">
                <span>{{ echeance.nom }}</span>
                <span class="badge badge-green">{{ echeance.statut }}</span>
              </div>
            </template>
            <div v-else class="dashboard-list-empty">Aucune échéance à venir</div>
          </div>
          <router-link to="/alertes?filter=attente" class="dashboard-link">Voir tout</router-link>
        </div>
      </div>
      <div class="dashboard-row">
        <div class="dashboard-card dashboard-card-full">
          <div class="dashboard-card-title">▪ Dernières factures</div>
          <div class="dashboard-list">
            <template v-if="dashboardData?.factures?.length">
              <div v-for="facture in dashboardData.factures" :key="facture.nom" class="dashboard-list-row">
                <span>{{ facture.nom }}</span>
                <span :class="['badge', 'badge-green']">Payée</span>
              </div>
            </template>
            <div v-else class="dashboard-list-empty">Aucune facture disponible</div>
          </div>
          <router-link to="/alertes?filter=reglee" class="dashboard-link">Voir tout</router-link>
        </div>
      </div>
    </div>
    <div class="dashboard-btn-row">
      <button class="dashboard-btn" @click="showCreateModal = true">Nouveau Contrat</button>
    </div>

    <ContratWizard 
      :show="showCreateModal"
      @close="showCreateModal = false"
      @contract-created="handleContratCreated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getDashboardData } from '@/services/dashboardService'
import ContratWizard from '@/components/contrats/ContratWizard.vue'

const dashboardData = ref(null)
const showCreateModal = ref(false)
const loading = ref(true)
const error = ref(null)

onMounted(async () => {
  await loadDashboardData()
})

async function loadDashboardData() {
  try {
    loading.value = true
    error.value = null
    dashboardData.value = await getDashboardData()
  } catch (err) {
    console.error('Error loading dashboard data:', err)
    error.value = 'Erreur lors du chargement des données. Veuillez réessayer.'
  } finally {
    loading.value = false
  }
}

// Fonction pour gérer la création d'un nouveau contrat
function handleContratCreated(newContrat) {
  console.log('Nouveau contrat créé:', newContrat)
  showCreateModal.value = false
  loadDashboardData() // Recharger les données du dashboard
}
</script>

<style scoped>
.dashboard-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-left: 270px;
  background: #f5f7fa;
  min-height: calc(100vh - 70px);
  padding: 24px 0 0 0;
}
.dashboard-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding-left: 24px;
}
.dashboard-icon {
  width: 38px;
  height: 38px;
  margin-right: 12px;
}
.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #22305a;
}
.dashboard-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.dashboard-row {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  padding-left: 24px;
}
.dashboard-btn-row {
  margin-top: auto;
  padding: 0 24px 24px 24px;
}
.dashboard-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 24px 24px 16px 24px;
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.dashboard-card-large {
  max-width: 260px;
  flex: none;
}
.dashboard-card-full {
  flex: 2;
}
.dashboard-card-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #22305a;
  margin-bottom: 18px;
}
.dashboard-card-value {
  font-size: 2.6rem;
  font-weight: 700;
  color: #22305a;
  margin-bottom: 18px;
}
.dashboard-card-value .big {
  font-size: 3.2rem;
  color: #22305a;
}
.dashboard-card-value .total {
  font-size: 2rem;
  color: #bfc7d5;
}
.dashboard-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dashboard-list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.1rem;
  color: #22305a;
}
.badge {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin-left: 12px;
}
.badge-orange {
  background: #e6a23c;
}
.badge-green {
  background: #3ba935;
}
.badge-red {
  background: #e74c3c;
}
.dashboard-link {
  color: #22305a;
  font-size: 0.95rem;
  text-decoration: underline;
  margin-top: 12px;
  align-self: flex-start;
  cursor: pointer;
  transition: color 0.2s;
}

.dashboard-link:hover {
  color: #003a8c;
  text-decoration: underline;
}
.dashboard-btn {
  width: 100%;
  background: #3ba935;
  color: #fff;
  font-size: 1.6rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  padding: 18px 0;
  cursor: pointer;
  transition: background 0.2s;
}
.dashboard-btn:hover {
  background: #2e8b2e;
}
.dashboard-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}
.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(0, 58, 140, 0.2);
  border-radius: 50%;
  border-top-color: #003a8c;
  animation: spin 1s infinite linear;
  margin-bottom: 1rem;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.dashboard-loading p {
  font-size: 1.2rem;
  color: #22305a;
}
.dashboard-error {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}
.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}
.dashboard-error p {
  font-size: 1.2rem;
  color: #e74c3c;
  margin-bottom: 1.5rem;
  text-align: center;
}
.retry-button {
  background: #3ba935;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}
.retry-button:hover {
  background: #2e8b2e;
}
.dashboard-list-empty {
  font-style: italic;
  color: #999;
  padding: 0.5rem 0;
}
@media (max-width: 900px) {
  .dashboard-row {
    flex-direction: column;
    gap: 12px;
    padding-left: 0;
  }
  .dashboard-btn-row {
    padding: 0 8px 24px 8px;
  }
  .dashboard-container {
    margin-left: 0;
  }
}
</style> 